import LogoWhite from '_content/images/logos/SuitsMe_Logo_Gradient.svg';
import LogoBlack from '_content/images/logos/SuitsMe_Logo_Gradient_White.svg';
import BackgroundTellMoney from '_content/images/tellmoney/tellGateway.svg';
import Typography from 'components/shared/generic/typography/presentational/Typography';
import Button from 'components/shared/generic/button/presentational/Button';
import DataCheck from 'components/shared/generic/dataCheck/presentational/DataCheck';
import { getTheme } from 'selectors/darkMode';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import useTranslate from 'lib/languages/useTranslate';
import {
    useCreateConsentMutation,
    useGetConsentQuery,
} from 'lib/services/tellGateway';
import TellGatewayAccountList from './components/TellGatewayAccountList';
import jwt_decode from 'jwt-decode';
import {
    ConsentOutcome,
    ConsentPaymentAccountType,
    ConsentScope,
} from 'lib/type';
import clsx from 'clsx';
import { hideModal, showModal } from 'actions/modal';
import { ERROR_MODAL } from 'constants/shared/modalTypes';
import { useNavigate } from 'react-router-dom';
import { endConsentSession } from 'utils/auth';
import TellGatewayAccountPermissions from './components/TellGatewayAccountPermissions';
import TellGatewayPaymentDetails from './components/TellGatewayPaymentDetails';
import TellGatewayConsentDetails from './components/TellGatewayConsentDetails';
import { useGetAccountQuery } from 'lib/services';

export const TellGateway = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const translate = useTranslate('consent');
    const consentId = sessionStorage.getItem('consentId') as string;
    const cognitoToken = localStorage.getItem('cognitoToken') as string;
    const tellGatewayCallBackUrl = sessionStorage.getItem(
        'tellGatewayCallBackUrl',
    ) as string;
    const {
        data: account,
        isFetching: isAccountFetching,
    } = useGetAccountQuery();
    const isDarkModeEnabled = useSelector(getTheme);
    const {
        data: consent,
        isLoading: isConsentLoading,
        error: consentError,
    } = useGetConsentQuery(consentId);
    const [
        createConsent,
        { isLoading: isLoadingCreateConsent, error: createConsentError },
    ] = useCreateConsentMutation();

    const isLoading = isAccountFetching || isConsentLoading;
    const error = (consentError || createConsentError) as {
        status: number;
        data: any;
    } | null;
    const logo = isDarkModeEnabled ? LogoBlack : LogoWhite;

    const handleNavigate = useCallback(
        async (url?: string) => {
            await endConsentSession();
            if (!url) {
                navigate('/');
                return;
            }
            globalThis.location.href = url;
        },
        [navigate],
    );

    const handleCreateConsent = async (outcome: ConsentOutcome) => {
        const { usernameV1: userId } = jwt_decode(cognitoToken);

        const payload = {
            ConsentId: consentId,
            Outcome: outcome,
            UserId: userId,
            Accounts: [String(account?.accountNumber)],
        };

        try {
            const response = await createConsent(payload).unwrap();
            await handleNavigate(response?.Location);
        } catch (error) {
            console.error('Consent creation error:', error);
        }
    };

    useEffect(() => {
        if (error) {
            dispatch(
                showModal({
                    type: ERROR_MODAL,
                    props: {
                        title: translate('errorTitle'),
                        message: translate('errorMessage'),
                        closeButtonText: translate('buttonContinue'),
                        onClose: () => {
                            void handleNavigate(tellGatewayCallBackUrl);
                            dispatch(hideModal());
                        },
                    },
                }),
            );
        }
    }, [error, dispatch, translate, tellGatewayCallBackUrl, handleNavigate]);

    const { Scope, PaymentInformation } = consent ?? {};

    const isPaymentConsent = Scope === ConsentScope.PAYMENTS;
    const isAccountConsent = Scope === ConsentScope.ACCOUNTS;
    const isFundsConfirmationsConsent =
        Scope === ConsentScope.FUNDS_CONFIRMATIONS;

    const getDescriptionText = () => {
        if (isPaymentConsent) {
            return translate('paymentDescription');
        }
        if (isFundsConfirmationsConsent) {
            return translate('fundsConfirmationsDescription');
        }
        return translate('accountDescription');
    };

    return (
        <DataCheck
            data={consent}
            isLoading={isLoading}
            loadingMessage={translate('loadingConsent')}
            className="tell-gateway-container"
            error={error?.data?.message ?? error?.data?.Error}
        >
            <img src={logo} alt="Logo" className="logo mt-4" />
            <img
                src={BackgroundTellMoney}
                alt="BackgroundTellMoney"
                className={clsx('overlay-image', {
                    'dark-mode': isDarkModeEnabled,
                })}
            />
            <div className="tell-gateway-inner-container">
                <div className="left-container">
                    <div className="header-info">
                        <Typography tag="h1">
                            {translate('headerText', {
                                accountName: account?.accountName,
                            })}
                        </Typography>
                        <Typography tag="p" className="header-description">
                            {getDescriptionText()}
                        </Typography>
                    </div>
                    <TellGatewayAccountList account={account} />
                </div>
                <div className="right-container">
                    <div className="right-container-inner">
                        <TellGatewayConsentDetails consentDetails={consent} />
                        {isAccountConsent && <TellGatewayAccountPermissions />}
                        {isPaymentConsent && (
                            <TellGatewayPaymentDetails
                                accountType={ConsentPaymentAccountType.CREDITOR}
                                paymentInformation={PaymentInformation}
                            />
                        )}
                        {isFundsConfirmationsConsent && (
                            <TellGatewayPaymentDetails
                                accountType={ConsentPaymentAccountType.DEBTOR}
                                paymentInformation={PaymentInformation}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className="tell-gateway-buttons">
                <Button
                    isPosting={isLoadingCreateConsent}
                    onClick={() => handleCreateConsent(ConsentOutcome.PASS)}
                >
                    {translate('buttonAccept')}
                </Button>
                <Button
                    className="reject-button"
                    isPosting={isLoadingCreateConsent}
                    onClick={() => handleCreateConsent(ConsentOutcome.FAIL)}
                >
                    {translate('buttonReject')}
                </Button>
            </div>
        </DataCheck>
    );
};
